import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { FaFolder, FaFileAlt, FaChevronLeft, FaChevronRight, FaCheck, FaTimes, FaSearch, FaUndo, FaEdit, FaEye, FaFolderOpen, FaCheckCircle, FaClock, FaExternalLinkAlt } from "react-icons/fa";
import Navbar from "../../components/navbar/Navbar";
import { useNavigate } from "react-router-dom";
import "./MyFilesTable.css";
import LoadingAnimation from "../../components/loading-animation/LoadingAnimation";
import dayjs from "dayjs"; // ➊ yarn add dayjs (or npm i dayjs)
import { isClientOrgUser as isClientOrgUserFn } from '../../utils/session';

// Fonction pour générer les numéros de pages avec ellipses
const getPageNumbers = (currentPage, totalPages, delta = 2) => {
  const range = [];
  const rangeWithDots = [];
  let l;

  const isClientOrgUser = isClientOrgUserFn();
if (isClientOrgUser && currentPage === 1) {
    // Page 1 : forme compacte 1 2 3 … 8 9 10 (pas de repère de lot suivant)
    const end = Math.min(10, totalPages);
    for (let i = 1; i <= totalPages; i++) {
      if (i <= 3 || (i >= end - 2 && i <= end)) {
        range.push(i);
      }
    }
  } else if (isClientOrgUser) {
    // Autres pages RMA : 1 … (courante-2 … courante) … repère du lot suivant
    //   ex. page 9 -> 1 … 7 8 9 … 20 ;  page 10 -> 1 … 8 9 10 … 20 ;  page 20 -> 1 … 18 19 20 … 30
    const nextBoundary = Math.min((Math.floor((currentPage - 1) / 10) + 2) * 10, totalPages);
    for (let i = 1; i <= totalPages; i++) {
      const nearStart = currentPage <= 3 && i <= 5;             // début : 1..5 restent lisibles
      const inWindow = i >= currentPage - 2 && i <= currentPage; // quelques pages avant + courante
      const isBoundary = i === nextBoundary;                     // saut vers la dizaine suivante
      // Pas de dernière page en dur : on ne montre que jusqu'à la dizaine suivante
      if (i === 1 || nearStart || inWindow || isBoundary) {
        range.push(i);
      }
    }
  } else {
    // Autres rôles : fenêtre symétrique classique autour de la page courante
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
};


// ---- Présentation uniquement : aucune règle métier ici ----
const TONES = ["indigo", "teal", "amber", "rose", "sky", "violet", "emerald"];
const toneOf = (txt) => {
  let h = 0;
  String(txt).split("").forEach((c) => { h = (h * 31 + c.charCodeAt(0)) % 997; });
  return TONES[h % TONES.length];
};

// Valeur texte affichée telle quelle ; les éléments (tiret vide) passent sans changement
const OrientationChip = ({ value }) =>
  typeof value === "string" && value.trim() ? (
    <span className={`orient-chip tone-${toneOf(value)}`}>{value}</span>
  ) : value;

const PersonCell = ({ value }) =>
  typeof value === "string" && value.trim() && value.trim() !== "—" ? (
    <span className="person">
      <span className={`avatar tone-${toneOf(value)}`}>{value.trim().charAt(0).toUpperCase()}</span>
      <span className="person-name">{value}</span>
    </span>
  ) : value;

const TauxBar = ({ value }) => {
  const n = parseFloat(String(value).replace("%", "").replace(",", "."));
  if (!isFinite(n)) return value;
  const w = Math.max(0, Math.min(100, n));
  const lvl = n === 0 ? "zero" : n < 50 ? "low" : n < 80 ? "mid" : "high";
  return (
    <span className={`taux lvl-${lvl}`} title={`${n}%`}>
      <span className="taux-track"><span className="taux-fill" style={{ width: `${w}%` }} /></span>
      <span className="taux-num">{n}%</span>
    </span>
  );
};

// Majuscule initiale à l'affichage du statut (« Validé par organisation »)
const cap = (txt) => (typeof txt === "string" && txt ? txt.charAt(0).toUpperCase() + txt.slice(1) : txt);

const STATUS_CHIPS_VEO = ["nouveau", "en cours", "validé", "sans suite"];

const DossiersDualTables = () => {
  // States for all dossiers, validated dossiers, search query, and error handling
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [allDossiers, setAllDossiers] = useState([]);
  const [validatedDossiers, setValidatedDossiers] = useState([]);
  const [error, setError] = useState(null);
  // Pagination states avec totaux
  const [pendingPage, setPendingPage] = useState(1);
  const [validatedPage, setValidatedPage] = useState(1);
  const [totalPendingCount, setTotalPendingCount] = useState(0);
  const [totalValidatedCount, setTotalValidatedCount] = useState(0);
  const itemsPerPage = 10; // Afficher 10 dossiers par page
  const pagesPerBatch = 10; // Nombre de pages regroupées par appel backend (RMA)
  // Cache des lots de 100 dossiers déjà récupérés côté RMA (clé = index de lot + filtres actifs)
  const [rmaBatchCache, setRmaBatchCache] = useState({});
  const [highestFetchedRmaBatch, setHighestFetchedRmaBatch] = useState(-1);
  // Filtres globaux (s'appliquent aux deux sections)
  const [searchQuery, setSearchQuery] = useState("");
  const [orientationFilter, setOrientationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(""); // Par défaut: afficher tous les dossiers
  const navigate = useNavigate();


  const [remoteSearchDossiers, setRemoteSearchDossiers] = useState([]);
  const [remoteSearchLoading, setRemoteSearchLoading] = useState(false);


  const normalizeQuery = (q) => (q || "").trim();

  const looksLikeWeproovCode = (q) => {
    const s = normalizeQuery(q);
    if (s.length < 4) return false;          // avoid hitting API for tiny inputs
    return /^[a-zA-Z0-9-_]+$/.test(s);       // adjust if your codes allow other chars
  };

  const looksLikeImmatriculation = (q) => {
    const s = normalizeQuery(q);
    if (s.length < 5) return false;
    return /^[A-Z0-9-]+$/i.test(s);
  };

  const fetchDossierByWeproov = async (code) => {
    const token = localStorage.getItem("access_token");
    const res = await axios.get(
      `/api/dossiers/by-weproov/${encodeURIComponent(code)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // backend returns a list always
    return Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
  };

  const EmptyCell = () => <span className="empty-cell" aria-label="Non renseigné">—</span>;

  const formatDate = (iso) => {
    if (!iso || iso === "—") return <EmptyCell />;
    let cleanedIso = String(iso).replace(" ", "T");
    cleanedIso = cleanedIso.split(".")[0];
    if (!cleanedIso) return <EmptyCell />;
    const d = dayjs(cleanedIso);
    if (!d.isValid()) return <EmptyCell />;
    return d.format("YYYY-MM-DD HH:mm");
  };


  const safeValue = (val) =>
    val !== null && val !== undefined && String(val).trim() !== ""
      ? val
      : <EmptyCell />;

  const isClientOrgUser = isClientOrgUserFn();
// Fonction pour convertir le statut du dropdown en statut(s) réel(s) pour le backend
  const getRealStatusForFilter = (displayStatus) => {
    if (!isClientOrgUser) return displayStatus;
    
    // Pour RMA, "nouveau" dans le dropdown correspond aux 3 statuts réels
    if (displayStatus === "nouveau") {
      return "nouveau"; // Le backend gérera cette valeur spéciale
    }
    // "validé" dans le dropdown correspond à "validé par organisation"
    if (displayStatus === "validé") {
      return "validé par organisation";
    }
    return displayStatus;
  };

  // Fetch all dossiers from the API
  const fetchAllDossiers = async () => {
    try {
      const token = localStorage.getItem("access_token");

      // Appel via le proxy /api (géré par Nginx) vers le backend FastAPI
      const endpoint = isClientOrgUser
        ? "/api/dossiers/rma"
        : "/api/dossiers";

      if (isClientOrgUser) {
        // ---- RMA : récupération par lots de 100 dossiers (10 pages), mis en cache ----
        const batchIndex = Math.floor((pendingPage - 1) / pagesPerBatch);
        const localIndex = (pendingPage - 1) % pagesPerBatch;
        const cacheKey = JSON.stringify({
          batchIndex,
          search: searchQuery.trim(),
          orientationFilter,
          statusFilter,
        });

        const cached = rmaBatchCache[cacheKey];
        if (cached) {
          setAllDossiers(
            cached.dossiers.slice(localIndex * itemsPerPage, localIndex * itemsPerPage + itemsPerPage)
          );
          setTotalPendingCount(cached.total);
          return;
        }

        const params = {
          page: batchIndex + 1,
          page_size: itemsPerPage * pagesPerBatch, // 100
        };
        if (searchQuery && searchQuery.trim()) {
          params.search = searchQuery.trim();
        }
        if (orientationFilter) {
          params.orientation = orientationFilter;
        }
        if (statusFilter) {
          params.status = getRealStatusForFilter(statusFilter);
        }

        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          params: params,
        });

        const dossiers = response.data.dossiers || [];
        const total = response.data.total || 0;

        setRmaBatchCache((prev) => ({ ...prev, [cacheKey]: { dossiers, total } }));

        setAllDossiers(dossiers.slice(localIndex * itemsPerPage, localIndex * itemsPerPage + itemsPerPage));
        setTotalPendingCount(total);
        console.log("RMA batch fetched:", batchIndex, response.data);
        return;
      }

      // ---- Veo (non-RMA) : inchangé, une page de 10 par appel ----
      const params = {
        page: pendingPage,
        page_size: itemsPerPage,
      };

      // Ajouter la recherche si présente
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Pour Veo : filtres par date et statut
      if (dateFilter) {
        const today = dayjs().startOf('day');
        const yesterday = dayjs().subtract(1, 'day').startOf('day');

        if (dateFilter === "today") {
          params.date_from = today.format('YYYY-MM-DD');
          params.date_to = today.format('YYYY-MM-DD');
        } else if (dateFilter === "yesterday") {
          params.date_from = yesterday.format('YYYY-MM-DD');
          params.date_to = yesterday.format('YYYY-MM-DD');
        }
      }
      // Filtre par statut pour VEO (colonne status)
      if (statusFilter) {
        params.status = statusFilter;
      }

      if (orientationFilter) {
        params.orientation = orientationFilter;
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        params: params,
      });

      setAllDossiers(response.data.dossiers || []);
      setTotalPendingCount(response.data.total || 0);
      console.log("All dossiers fetched:", response.data);
    } catch (err) {
      console.error("Error fetching all dossiers:", err);
      setError("Erreur lors du chargement des dossiers.");
    }
  };
  // Fetch only validated dossiers from the API
  const fetchValidatedDossiers = async () => {
    try {
      if (isClientOrgUser) {
        setValidatedDossiers([]); // not needed for RMA
        return;
      }

      const token = localStorage.getItem("access_token");
      
      // Construire les paramètres de date si un filtre est actif
      const params = {
        page: validatedPage,
        page_size: itemsPerPage,
      };
      
      // Ajouter la recherche si présente
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      if (dateFilter) {
        const today = dayjs().startOf('day');
        const yesterday = dayjs().subtract(1, 'day').startOf('day');
        
        if (dateFilter === "today") {
          params.date_from = today.format('YYYY-MM-DD');
          params.date_to = today.format('YYYY-MM-DD');
        } else if (dateFilter === "yesterday") {
          params.date_from = yesterday.format('YYYY-MM-DD');
          params.date_to = yesterday.format('YYYY-MM-DD');
        }
      }
      
      // Filtre par statut pour les dossiers validés
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      if (orientationFilter) {
        params.orientation = orientationFilter;
      }

      const response = await axios.get(
        "/api/dossiers/validé",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: params,
        }
      );
      console.log("Validated dossiers fetched:", response.data);

      setValidatedDossiers(response.data.dossiers || []);
      setTotalValidatedCount(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching validated dossiers:", err);
      setError("Erreur lors du chargement des dossiers validés.");
    }
  };


  // Debounced search - attend 500ms avant de lancer la requête
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);



  useEffect(() => {
    const refresh = () => {
      setIsFetching(true);
      Promise.all([fetchAllDossiers(), fetchValidatedDossiers()]).finally(() => {
        setIsFetching(false);
        setLastUpdated(dayjs());
      });
    };

    // Initial API requests
    refresh();

    // Set up polling to refresh dossier data every 60 minutes (3600000 ms)
    const pollInterval = setInterval(refresh, 3600000);

    // Clean up the interval on component unmount
    return () => clearInterval(pollInterval);
  }, [dateFilter, orientationFilter, statusFilter, pendingPage, validatedPage, debouncedSearch]); // Re-fetch when filters or pages change

  // Helper function to render icons based on file type
  const renderFileTypeIcon = (fileType) =>
    fileType === "folder" ? <FaFolder /> : <FaFileAlt />;

  const getStatusClass = (status) => {
    if (typeof status !== "string") return "status-pill";
    
    const s = status.toLowerCase();
    
    // Define status mappings based on user type
    const rmaStatusMap = {
      "en cours": "status-pill active",
      "validé": "status-pill active",
      "nouveau": "status-pill active",
      "validé par organisation": "status-pill validated-rma",
      "sans suite": "status-pill active"
    };
    
    const nonRmaStatusMap = {
      "en cours": "status-pill en-cours",
      "validé": "status-pill active",
      "nouveau": "status-pill done",
      "validé par organisation": "status-pill validated-rma",
      "sans suite": "status-pill sans-suite"
    };
    
    const statusMap = isClientOrgUser ? rmaStatusMap : nonRmaStatusMap;
    
    return statusMap[s] || "status-pill";
  };

  // Helpers to get display text and CSS classes for dossier status
  const getStatusText = (status) => {
    if (typeof status === "string") {
      const s = status.toLowerCase();

      if (isClientOrgUser) {
        if (s === "validé" || s === "en cours" || s === "nouveau" || s === "sans suite") return "nouveau";
        if (s === "validé par organisation") return "validé";
      }

      return status;
    }
    return "—";
  };

  // Helper to determine if a dossier is verified
  const isDossierVerified = (status) => {
    if (typeof status !== "string") return false;
    const s = status.toLowerCase();
    // Seulement "validé" est vérifié (pas "validé par organisation")
    return s === "validé" || s === "sans suite";
  };

  // Helper to determine if badge should be shown
  const shouldShowBadge = (status) => {
    if (typeof status !== "string") return false;
    const s = status.toLowerCase();
    // Pas de badge pour "validé par organisation"
    return s !== "validé par organisation";
  };

  const getUniqueOrientations = () => {
    const allDocsArray = Array.isArray(allDossiers) ? allDossiers : [];
    const validatedDocsArray = Array.isArray(validatedDossiers) ? validatedDossiers : [];
    
    const allOrientations = [...allDocsArray, ...validatedDocsArray]
      .flatMap((dossier) => [
        dossier.orientation,
        dossier.orientation_organisation,
        dossier.orientation_veo
      ])
      .filter((orientation) => orientation && orientation !== "—");
    return [...new Set(allOrientations)];
  };

  const handleSuperviseDossier = async (numeroDuDossier) => {
    console.log("Supervising dossier with code:", numeroDuDossier);

    setIsLoading(true);

    if (!numeroDuDossier) {
      alert("Veuillez entrer un code WeProov valide.");
      setIsLoading(false);
      return;
    }

    // Pas de contrôle d'appartenance par `user_id` ici : un dossier appartient à
    // une organisation, et tous les users de cette organisation peuvent le
    // superviser. Le serveur tranche (`assert_can_access_dossier`), et la liste
    // est déjà cloisonnée — un dossier visible est un dossier accessible.

    try {
      const updateResponse = await fetch(
        `/api/dossiers/supervise/${numeroDuDossier}`,
        {
          method: "PUT",
          headers: {
            "user-id": localStorage.getItem("user_id"),
            "Content-Type": "application/json",
          },
        }
      );
      const updateData = await updateResponse.json();
      if (!updateResponse.ok) throw new Error("Erreur API supervise");

      console.log("Dossier supervisé:", updateData);

      navigate(`/result-page/${numeroDuDossier}`);
    } catch (error) {
      console.error("Error supervising dossier:", error);
      alert("Erreur lors du traitement du dossier.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateOnly = (numeroDuDossier) => {
    if (!numeroDuDossier) return;
    navigate(`/result-page/${numeroDuDossier}`);
  };

  const getOrientationByStatus = (dossier) => {
    const statusRma = (dossier.status_organisation || "").toLowerCase();
    const status = (dossier.status || "").toLowerCase();

    // Côté client : orientation finale si l'organisation a validé, sinon
    // orientation Veo si Veo a traité le dossier (validé ou sans suite), sinon IA.
    if (isClientOrgUser) {
      const statusVeo = (dossier.status_veo || "").toLowerCase();
      if (statusRma === "validé par organisation") return safeValue(dossier.orientation_organisation);
      if (statusVeo === "validé" || statusVeo === "sans suite")
        return safeValue(dossier.orientation_veo || dossier.orientation);
      return safeValue(dossier.orientation);
    }

    // 1) status_organisation = "validé par organisation" → orientation_organisation
    if (statusRma === "validé par organisation") return safeValue(dossier.orientation_organisation);

    // 2) status_organisation vide + status = nouveau/en cours → orientation (IA)
    if (!statusRma && (status === "nouveau" || status === "en cours"))
      return safeValue(dossier.orientation);

    // 3) status_organisation vide + status = validé → orientation_veo
    if (!statusRma && status === "validé")
      return safeValue(dossier.orientation_veo);

    // 4) status = sans suite → orientation_veo
    //if (status === "sans suite") return safeValue(dossier.orientation_veo);

    return safeValue(dossier.orientation); // fallback
  };

  // Même règle que l'orientation : si l'organisation a validé, on affiche sa
  // validation (date + validateur) ; sinon celle de VeoSmart.
  const getValidationInfo = (dossier) => {
    const statusOrg = (dossier.status_organisation || "").toLowerCase();
    if (statusOrg === "validé par organisation") {
      const user = dossier.validated_by_in_organisation;
      const org = dossier.validated_by_organisation_name;
      return {
        date: dossier.date_validation_organisation,
        by: user ? (org ? `${user} (${org})` : user) : null,
      };
    }
    return { date: dossier.finished_at, by: dossier.validated_by };
  };

  // Les données viennent directement du backend, déjà triées et paginées
  const currentPending = isClientOrgUser ? [] : (Array.isArray(allDossiers) ? allDossiers : []);
  const currentValidated = Array.isArray(validatedDossiers) ? validatedDossiers : [];
  const currentRma = isClientOrgUser ? (Array.isArray(allDossiers) ? allDossiers : []) : [];
  
  // Calcul des pages basé sur les totaux retournés par le backend
  const totalPendingPages = Math.ceil(totalPendingCount / itemsPerPage);
  const totalValidatedPages = Math.ceil(totalValidatedCount / itemsPerPage);
  const totalRmaPages = Math.ceil(totalPendingCount / itemsPerPage); // Pour RMA, même logique

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    // Reset à la page 1 lors d'une recherche
    setPendingPage(1);
    setValidatedPage(1);
  };



  // Reset pagination when filters change
  useEffect(() => {
    setPendingPage(1);
    setValidatedPage(1);
    // Les filtres changent le résultat : le cache de lots RMA n'est plus valable
    setRmaBatchCache({});
  }, [searchQuery, orientationFilter, statusFilter, dateFilter]);

  return (
    <div className="my-files-container">
      <Navbar username={localStorage.getItem("username") || "Guest"}/>
      <div className="content-offset">
        <div className="header-row">
          <h2 className="header-title">
            {isClientOrgUser ? (
              <>Tous les dossiers <span className="count-badge">{totalPendingCount}</span></>
            ) : (
              <>Dossiers</>
            )}
          </h2>
          {lastUpdated && (
            <p className="header-sub" aria-live="polite">
              {isFetching ? "Mise à jour…" : `Mis à jour à ${lastUpdated.format("HH:mm")}`}
            </p>
          )}
        </div>

        <div className="toolbar">
          <div className="chips" role="group" aria-label="Filtrer par statut">
            <button type="button" className={`chip${statusFilter === "" ? " on" : ""}`} onClick={() => setStatusFilter("")}>Tous</button>
            {(isClientOrgUser ? ["nouveau", "validé"] : STATUS_CHIPS_VEO).map((st) => (
              <button
                key={st}
                type="button"
                className={`chip${statusFilter === st ? " on" : ""}`}
                onClick={() => setStatusFilter(st)}
              >
                {st.charAt(0).toUpperCase() + st.slice(1)}
              </button>
            ))}
          </div>
          <div className="filters-container">
            <div className="search-bar">
              <FaSearch className="search-icon" aria-hidden="true" />
              <input
                type="search"
                placeholder="Rechercher dans toutes les sections"
                aria-label="Rechercher un dossier"
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
            <div className="filter-dropdown">
              <select
                value={orientationFilter}
                onChange={(e) => setOrientationFilter(e.target.value)}
                className="filter-select"
                aria-label="Filtrer par orientation"
              >
                <option value="">Toutes orientations</option>
                {getUniqueOrientations().map((orientation) => (
                  <option key={orientation} value={orientation}>
                    {orientation}
                  </option>
                ))}
              </select>
            </div>
            {(searchQuery || orientationFilter || statusFilter) && (
              <button
                type="button"
                className="filters-reset"
                onClick={() => {
                  setSearchQuery("");
                  setOrientationFilter("");
                  setStatusFilter("");
                }}
              >
                <FaUndo aria-hidden="true" /> Réinitialiser
              </button>
            )}
          </div>
        </div>

        {error && <p className="error-message" role="alert">{error}</p>}

        {/* ---------- VUE RMA : UNE SEULE TABLE MERGÉE ---------- */}
        {isClientOrgUser ? (
          <>
            <div className="table-scroll md-card" aria-busy={isFetching}>
            <table className="my-files-table">
              <thead>
                <tr>
                  <th>Immatriculation</th>
                  <th>Date de sinistre</th>
                  <th>Date de création</th>
                  <th>Date de validation</th>
                  <th>Validé par</th>
                  <th>Orientation</th>
                  <th>Statut</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentRma.length > 0 ? (
                  currentRma.map((file) => (
                    <tr key={file.id} className={`row-${String(getStatusText(file.status) || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <td className="num">{safeValue(file.immatriculation)}</td>
                      <td className="num">{safeValue(file.date_de_sinistre)}</td>
                      <td className="num">{formatDate(file.created_at || "—")}</td>
                      <td className="num">
                        {formatDate(
                          file.date_validation_organisation ||
                           
                            "—"
                        )}
                      </td>
                      <td><PersonCell value={safeValue(file.validated_by_in_organisation)} /></td>
                      <td><OrientationChip value={getOrientationByStatus(file)} /></td>
                      <td>
                        <div className="status-cell">
                          <span className={getStatusClass(file.status)}>
                            {cap(getStatusText(file.status))}
                          </span>
                          {shouldShowBadge(file.status) && (
                            isDossierVerified(file.status) ? (
                              <span className="verification-badge verified" title="Dossier vérifié">
                                <FaCheck />
                              </span>
                            ) : (
                              <span className="verification-badge not-verified" title="Dossier non vérifié">
                                <FaTimes />
                              </span>
                            )
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn-supervised secondary"
                          title="Consulter"
                          onClick={() =>
                            handleNavigateOnly(file.numero_du_dossier)
                          }
                        >
                          <FaEye aria-hidden="true" /> Consulter
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8}><FaFolderOpen className="empty-ico" aria-hidden="true" />Aucun dossier trouvé.</td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination RMA */}
            {totalRmaPages > 1 && (
              <div className="pagination-wrap">
              <span className="page-info">Page {pendingPage} sur {totalRmaPages}</span>
              <nav className="pagination" aria-label="Pagination">
                <button
                  onClick={() =>
                    setPendingPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={pendingPage === 1}
                >
                  <FaChevronLeft size={14} />
                </button>

                {getPageNumbers(pendingPage, totalRmaPages).map((pageNumber, index) => (
                  pageNumber === '...' ? (
                    <span key={`dots-${index}`} className="pagination-dots">...</span>
                  ) : (
                    <button
                      key={pageNumber}
                      onClick={() => setPendingPage(pageNumber)}
                      className={pageNumber === pendingPage ? "active-page" : ""}
                    >
                      {pageNumber}
                    </button>
                  )
                ))}

                <button
                  onClick={() =>
                    setPendingPage((prev) =>
                      Math.min(prev + 1, totalRmaPages)
                    )
                  }
                  disabled={pendingPage === totalRmaPages}
                >
                  <FaChevronRight size={14} />
                </button>
              </nav>
              </div>
            )}

            {isLoading && <LoadingAnimation />}
          </>
        ) : (
          <>
            {/* ---------- VUE NON-RMA : TABLE 1 (EN COURS / NOUVEAUX) ---------- */}
            <div className="header-row">
              <h3 className="section-title pending"><FaClock aria-hidden="true" /> En cours / nouveaux <span className="count-badge">{totalPendingCount}</span></h3>
            </div>
            <div className="table-scroll md-card" aria-busy={isFetching}>
            <table className="my-files-table">
              <thead>
                <tr>
                  <th>Proov Code</th>
                  <th>Immatriculation</th>
                  <th>Organisation</th>
                  <th>Date de création</th>
                  <th>Date d'ouverture</th>
                  <th>Traité par</th>
                  <th>Orientation</th>
                  <th>RC (%)</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentPending.length > 0 ? (
                  currentPending.map((file) => (
                    <tr key={file.id} className={`row-${String(getStatusText(file.status) || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <td>
                        <span className="code-cell">
                          {renderFileTypeIcon("folder")}
                          {file.numero_du_dossier}
                        </span>
                      </td>
                      <td className="num">{safeValue(file.immatriculation)}</td>
                      <td>{safeValue(file.organisation_name)}</td>
                      <td className="num">{formatDate(file.created_at || "—")}</td>
                      <td className="num">{formatDate(file.updated_at || "—")}</td>
                      <td><PersonCell value={safeValue(file.treated_by_username)} /></td>
                      <td><OrientationChip value={getOrientationByStatus(file)} /></td>
                      <td>
                        <TauxBar value={safeValue(file.taux)} />
                      </td>
                      <td>
                        <span className={getStatusClass(file.status)}>
                          {cap(getStatusText(file.status))}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-supervised"
                          title="Editer"
                          onClick={() =>
                            handleSuperviseDossier(file.numero_du_dossier)
                          }
                        >
                          <FaEdit aria-hidden="true" /> Editer
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10}><FaFolderOpen className="empty-ico" aria-hidden="true" />Aucun dossier en cours ou nouveau trouvé.</td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination for Pending Dossiers */}
            {totalPendingPages > 1 && (
              <div className="pagination-wrap">
              <span className="page-info">Page {pendingPage} sur {totalPendingPages}</span>
              <nav className="pagination" aria-label="Pagination">
                <button
                  onClick={() =>
                    setPendingPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={pendingPage === 1}
                >
                  <FaChevronLeft size={14} />
                </button>

                {getPageNumbers(pendingPage, totalPendingPages).map((pageNumber, index) => (
                  pageNumber === '...' ? (
                    <span key={`dots-${index}`} className="pagination-dots">...</span>
                  ) : (
                    <button
                      key={pageNumber}
                      onClick={() => setPendingPage(pageNumber)}
                      className={pageNumber === pendingPage ? "active-page" : ""}
                    >
                      {pageNumber}
                    </button>
                  )
                ))}

                <button
                  onClick={() =>
                    setPendingPage((prev) =>
                      Math.min(prev + 1, totalPendingPages)
                    )
                  }
                  disabled={pendingPage === totalPendingPages}
                >
                  <FaChevronRight size={14} />
                </button>
              </nav>
              </div>
            )}

            {isLoading && <LoadingAnimation />}

            {/* ---------- VUE NON-RMA : TABLE 2 (VALIDÉS) ---------- */}
            <div className="header-row section-row">
              <div className="header-text">
                <h3 className="section-title"><FaCheckCircle aria-hidden="true" /> Dossiers Validés <span className="count-badge">{totalValidatedCount}</span></h3>
              </div>
            </div>
            <div className="table-scroll md-card" aria-busy={isFetching}>
            <table className="my-files-table">
              <thead>
                <tr>
                  <th>Proov Code</th>
                  <th>Immatriculation</th>
                  <th>Date de sinistre</th>
                  <th>Date de validation</th>
                  <th>Validé par</th>
                  <th>Orientation</th>
                  <th>Statut</th>
                  <th>Rapports</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentValidated.length > 0 ? (
                  currentValidated.map((file) => (
                    <tr key={file.id} className={`row-${String(getStatusText(file.status) || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <td>
                        <span className="code-cell">
                          {renderFileTypeIcon("folder")}
                          {file.numero_du_dossier}
                        </span>
                      </td>
                      <td className="num">{safeValue(file.immatriculation)}</td>
                      <td className="num">{safeValue(file.date_de_sinistre)}</td>
                      <td className="num">{formatDate(getValidationInfo(file).date || "—")}</td>
                      <td><PersonCell value={safeValue(getValidationInfo(file).by)} /></td>
                      <td><OrientationChip value={getOrientationByStatus(file)} /></td>

                      <td>
                        <span className={getStatusClass(file.status)}>
                          {cap(getStatusText(file.status))}
                        </span>
                      </td>
                      <td>
                        <span className="reports">
                          {file.pdf_link && file.pdf_link !== "—" ? (
                            <a href={file.pdf_link} target="_blank" rel="noopener noreferrer" className="report-link" aria-label="Voir le rapport IA (nouvel onglet)">
                              IA <FaExternalLinkAlt aria-hidden="true" />
                            </a>
                          ) : (
                            <span className="report-link off" aria-label="Rapport IA indisponible">IA</span>
                          )}
                          {file.weproov_url && file.weproov_url !== "—" ? (
                            <a href={file.weproov_url} target="_blank" rel="noopener noreferrer" className="report-link" aria-label="Voir le rapport WeProov (nouvel onglet)">
                              WeProov <FaExternalLinkAlt aria-hidden="true" />
                            </a>
                          ) : (
                            <span className="report-link off" aria-label="Rapport WeProov indisponible">WeProov</span>
                          )}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-supervised secondary"
                          title="Consulter"
                          onClick={() =>
                            handleNavigateOnly(file.numero_du_dossier)
                          }
                        >
                          <FaEye aria-hidden="true" /> Consulter
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9}><FaFolderOpen className="empty-ico" aria-hidden="true" />Aucun dossier validé trouvé.</td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination for Validated Dossiers */}
            {totalValidatedPages > 1 && (
              <div className="pagination-wrap">
              <span className="page-info">Page {validatedPage} sur {totalValidatedPages}</span>
              <nav className="pagination" aria-label="Pagination">
                <button
                  onClick={() =>
                    setValidatedPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={validatedPage === 1}
                >
                  <FaChevronLeft size={14} />
                </button>

                {getPageNumbers(validatedPage, totalValidatedPages).map((pageNumber, index) => (
                  pageNumber === '...' ? (
                    <span key={`dots-${index}`} className="pagination-dots">...</span>
                  ) : (
                    <button
                      key={pageNumber}
                      onClick={() => setValidatedPage(pageNumber)}
                      className={pageNumber === validatedPage ? "active-page" : ""}
                    >
                      {pageNumber}
                    </button>
                  )
                ))}

                <button
                  onClick={() =>
                    setValidatedPage((prev) =>
                      Math.min(prev + 1, totalValidatedPages)
                    )
                  }
                  disabled={validatedPage === totalValidatedPages}
                >
                  <FaChevronRight size={14} />
                </button>
              </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DossiersDualTables;
